import { createContext, ReactNode, useEffect, useState } from 'react';

import type { UserResponse } from '@repo/contracts/tenant-api';

import { useGetCurrentUserQuery } from '@/features/users/api';
import { useAuth } from '@/lib/auth';

/**
 * TEMPORARY transition shim — the airline→organization role rename (task 071) is
 * not yet settled team-wide, and existing environments (e.g. staging) still carry
 * the legacy "Airline *" role names in the DB. A `hasRole('Organization Admin')`
 * check accepts the legacy alias so access works for users on EITHER name.
 * Remove this whole block (and revert `hasRole` to a plain exact match) once the
 * role naming is finalised and the role data is migrated.
 */
const LEGACY_ROLE_ALIASES: Record<string, readonly string[]> = {
  'Organization Admin': ['Organization Admin', 'Airline Admin'],
  'Organization User': ['Organization User', 'Airline User'],
};

export type UserRole = {
  name: string;
  scope: {
    type: string;
    id: string;
  };
};

export type CurrentUser = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  roles: UserRole[];
};

type CurrentUserContextType = {
  currentUser: CurrentUser | null;
  setUser: (user: CurrentUser) => void;
  clearUser: () => void;
  hasRole: (roleName: string) => boolean;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
};

export const CurrentUserContext = createContext<
  CurrentUserContextType | undefined
>(undefined);

function mapUserResponseToCurrentUser(response: UserResponse): CurrentUser {
  return {
    id: response.id,
    email: response.email,
    firstName: response.firstName ?? '',
    lastName: response.lastName ?? '',
    roles: (response.userRoles ?? []).map((ur) => ({
      name: ur.role.name,
      scope: {
        type: ur.role.scopeType ?? '',
        id: ur.role.scopeId ?? '',
      },
    })),
  };
}

export const CurrentUserProvider = ({ children }: { children: ReactNode }) => {
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);

  // Fetch user profile when authenticated.
  // Global scope headers are already set by CurrentTenantProvider (parent).
  const {
    data: userResponse,
    isLoading: isUserFetching,
    isError,
    error: userError,
    refetch: refetchUser,
  } = useGetCurrentUserQuery(undefined, {
    skip: !isAuthenticated || isAuthLoading,
  });

  useEffect(() => {
    if (userResponse) {
      setCurrentUser(mapUserResponseToCurrentUser(userResponse));
    } else if (!isAuthenticated && !isAuthLoading) {
      setCurrentUser(null);
    }
  }, [userResponse, isAuthenticated, isAuthLoading]);

  const setUser = (user: CurrentUser) => {
    setCurrentUser(user);
  };

  const clearUser = () => {
    setCurrentUser(null);
  };

  const hasRole = (roleName: string) => {
    // See LEGACY_ROLE_ALIASES — temporary airline→organization transition shim.
    const accepted = LEGACY_ROLE_ALIASES[roleName] ?? [roleName];
    return !!currentUser?.roles.some((r) => accepted.includes(r.name));
  };

  const isLoading = isAuthLoading || isUserFetching;

  return (
    <CurrentUserContext.Provider
      value={{
        currentUser,
        setUser,
        clearUser,
        hasRole,
        isLoading,
        isError,
        error: userError,
        refetch: refetchUser,
      }}
    >
      {children}
    </CurrentUserContext.Provider>
  );
};
